import { ITimeSlotApiResponse, TimeSlot } from "../models/timeslot";

export const buildMockSlots = (
  start: Date,
  end: Date,
  timeZone: string,
  slotDurationMin: number,
  providerIds: number[],
): ITimeSlotApiResponse[] => {
  if (!(start instanceof Date) || !(end instanceof Date) || +end <= +start) return [];

  // create two local times per day
  const hours = [10, 15]; // tweak if desired

  // Anchors at local noon to get stable day offsets (DST-safe)
  const sYmd = ymdInTz(start, timeZone);
  const eYmd = ymdInTz(end, timeZone);
  const startNoonUtc = zonedToUtcMs(sYmd.y, sYmd.m, sYmd.d, 12, 0, timeZone);
  const endNoonUtc = zonedToUtcMs(eYmd.y, eYmd.m, eYmd.d, 12, 0, timeZone);

  const dayCount = Math.max(0, Math.ceil((endNoonUtc - startNoonUtc) / 86_400_000)); // 24h steps
  const participants = providerIds.map((id) => ({ provider_id: id.toString() }));

  return Array.from({ length: dayCount }).flatMap((_, i) => {
    const dayMidUtc = startNoonUtc + i * 86_400_000;
    const ymd = ymdInTz(new Date(dayMidUtc), timeZone);

    return hours.map((h) => {
      const startMs = zonedToUtcMs(ymd.y, ymd.m, ymd.d, h, 0, timeZone);
      const endMs = startMs + slotDurationMin * 60_000;

      // Using structural typing; TimeSlot ctor not required here
      const slot: ITimeSlotApiResponse = {
        start: new Date(startMs),
        end: new Date(endMs),
        participants,
      } as ITimeSlotApiResponse;

      return slot;
    });
  });
}

const ymdInTz = (d: Date, timeZone: string): { y: number; m: number; d: number; } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const num = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return { y: num('year'), m: num('month'), d: num('day') };
}

const zoneOffsetMsAt = (utcMs: number, timeZone: string): number => {
  // Offset between this UTC instant and how it looks in `timeZone`
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(new Date(utcMs));

  const get = (t: Intl.DateTimeFormatPartTypes) => Number(p.find((q) => q.type === t)?.value ?? '0');
  const asUtcMs = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtcMs - utcMs;
}

const zonedToUtcMs = (y: number, m: number, d: number, h: number, mi: number, timeZone: string): number => {
  // Use midday anchor to capture the day's DST offset
  const anchor = Date.UTC(y, m - 1, d, 12, 0, 0);
  const offset = zoneOffsetMsAt(anchor, timeZone);
  return Date.UTC(y, m - 1, d, h, mi, 0) - offset;
}